// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/IdleTeamChart.tsx

import { useMemo, useState } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  LabelList,
  Legend,
} from 'recharts';

import type {
  IdleTeamChartResponse,
} from '../../../types/Manager/idleTime.types';

interface Props {
  data: IdleTeamChartResponse[];
}

type HoveredBar =
  | 'thisWeekIdle'
  | 'lastWeekIdle'
  | null;

/* ============================================================
   NAME HELPERS
   ============================================================ */

const cleanName = (
  value: string,
): string => {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
};

/* ============================================================
   SINGLE WORD TRUNCATION
   ============================================================ */

const truncateWord = (
  value: string,
  teamCount: number,
): string => {
  const word = cleanName(value);

  if (!word) {
    return '';
  }

  /*
   * 5 teams need smaller labels.
   * 6+ teams have their own wider chart area.
   */
  const maxLength =
    teamCount <= 4
      ? 14
      : teamCount === 5
        ? 10
        : 14;

  if (word.length <= maxLength) {
    return word;
  }

  return `${word.slice(
    0,
    Math.max(1, maxLength - 3),
  )}...`;
};

/* ============================================================
   FIRST LINE
   ============================================================ */

const truncateFirstLine = (
  value: string,
  teamCount: number,
): string => {
  return truncateWord(
    value,
    teamCount,
  );
};

/* ============================================================
   SECOND LINE
   ============================================================ */

const truncateSecondLine = (
  value: string,
  teamCount: number,
): string => {
  const text = cleanName(value);

  if (!text) {
    return '';
  }

  const maxLength =
    teamCount <= 4
      ? 15
      : teamCount === 5
        ? 11
        : 15;

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(
    0,
    Math.max(1, maxLength - 3),
  )}...`;
};

/* ============================================================
   GET NAME LINES
   ============================================================ */

const getNameLines = (
  value: string,
  teamCount: number,
): string[] => {
  const name = cleanName(value);

  if (!name) {
    return [''];
  }

  const words = name.split(' ');

  /*
   * Single word
   */
  if (words.length === 1) {
    return [
      truncateWord(
        words[0],
        teamCount,
      ),
    ];
  }

  /*
   * First word stays on first line.
   */
  const firstLine =
    truncateFirstLine(
      words[0],
      teamCount,
    );

  /*
   * Everything else goes to second line.
   */
  const remaining =
    words
      .slice(1)
      .join(' ');

  const secondLine =
    truncateSecondLine(
      remaining,
      teamCount,
    );

  return [
    firstLine,
    secondLine,
  ];
};

/* ============================================================
   CUSTOM X AXIS TICK
   ============================================================ */

const CustomXAxisTick = ({
  x,
  y,
  payload,
  teamCount,
}: any) => {
  const count = Number(
    teamCount ?? 0,
  );

  const fullName = cleanName(
    String(
      payload?.value ?? '',
    ),
  );

  const lines =
    getNameLines(
      fullName,
      count,
    );

  /*
   * Every team gets its own label area.
   */
  const labelWidth =
    count <= 4
      ? 100
      : count === 5
        ? 68
        : 88;

  /*
   * Unique clip ID.
   */
  const safeX =
    Math.round(
      Number(x) || 0,
    );

  const safeY =
    Math.round(
      Number(y) || 0,
    );

  const clipId =
    `idle-team-label-${count}-${safeX}-${safeY}`
      .replace(
        /[^a-zA-Z0-9_-]/g,
        '-',
      );

  return (
    <g
      transform={`translate(${x},${y})`}
    >
      <defs>
        <clipPath id={clipId}>
          <rect
            x={
              -labelWidth / 2
            }
            y={0}
            width={labelWidth}
            height={38}
          />
        </clipPath>
      </defs>

      <text
        x={0}
        y={0}
        textAnchor="middle"
        fill="#64748B"
        fontSize={
          count === 5
            ? 9
            : 10
        }
        fontWeight={400}
        clipPath={`url(#${clipId})`}
      >
        <tspan
          x={0}
          dy={10}
        >
          {lines[0]}
        </tspan>

        {lines[1] && (
          <tspan
            x={0}
            dy={12}
          >
            {lines[1]}
          </tspan>
        )}

        {/* Full name appears on native SVG hover. */}
        <title>
          {fullName}
        </title>
      </text>
    </g>
  );
};

/* ============================================================
   TARGET LABEL
   ============================================================ */

const TargetLabel = ({
  viewBox,
  value,
}: {
  viewBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  value: number;
}) => {
  if (!viewBox) {
    return null;
  }

  const chartX =
    Number(viewBox.x ?? 0);

  const chartWidth =
    Number(viewBox.width ?? 0);

  const chartY =
    Number(viewBox.y ?? 0);

  const labelWidth = 72;
  const labelHeight = 21;

  const x =
    chartX +
    chartWidth -
    labelWidth -
    4;

  const y =
    chartY -
    labelHeight / 2;

  return (
    <g
      transform={`translate(${x},${y})`}
    >
      <rect
        x={0}
        y={0}
        width={labelWidth}
        height={labelHeight}
        rx={4}
        fill="#FFFFFF"
        stroke="#FCA5A5"
        strokeWidth={1}
      />

      <line
        x1={6}
        y1={10.5}
        x2={14}
        y2={10.5}
        stroke="#EF4444"
        strokeWidth={2}
        strokeDasharray="4 3"
      />

      <text
        x={18}
        y={14}
        fill="#EF4444"
        fontSize={10}
        fontWeight={600}
      >
        Target {value}%
      </text>
    </g>
  );
};

/* ============================================================
   CUSTOM TOOLTIP
   ============================================================ */

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const item =
    payload[0];

  if (!item) {
    return null;
  }

  const row =
    item.payload as IdleTeamChartResponse;

  const dataKey =
    item.dataKey as
      | 'thisWeekIdle'
      | 'lastWeekIdle';

  const isThisWeek =
    dataKey ===
    'thisWeekIdle';

  const value =
    isThisWeek
      ? Number(
          row.thisWeekIdle ?? 0,
        )
      : Number(
          row.lastWeekIdle ?? 0,
        );

  const barColor =
    isThisWeek
      ? '#FF8C32'
      : '#FFD54F';

  const label =
    isThisWeek
      ? 'This Week Idle'
      : 'Last Week Idle';

  return (
    <div
      className="
        min-w-[175px]
        max-w-[240px]
        overflow-hidden
        rounded-lg
        border
        border-slate-200
        bg-white
        shadow-lg
      "
    >
      <div
        className="
          border-b
          border-slate-200
          bg-slate-50
          px-3
          py-2
        "
      >
        <p
          className="
            break-words
            text-[12px]
            font-semibold
            leading-4
            text-slate-800
          "
        >
          {row.teamLeadName}
        </p>
      </div>

      <div
        className="
          px-3
          py-2.5
        "
      >
        <div
          className="
            flex
            items-center
            justify-between
            gap-5
          "
        >
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <span
              className="
                h-2.5
                w-2.5
                shrink-0
                rounded-full
              "
              style={{
                backgroundColor:
                  barColor,
              }}
            />

            <span
              className="
                text-[11px]
                text-slate-600
              "
            >
              {label}
            </span>
          </div>

          <span
            className="
              shrink-0
              text-[12px]
              font-bold
              text-slate-800
            "
          >
            {value.toFixed(2)}%
          </span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const IdleTeamChart = ({
  data,
}: Props) => {
  /*
   * Always protect against undefined/null API response.
   */
  const chartData =
    Array.isArray(data)
      ? data
      : [];

  const teamCount =
    chartData.length;

  const hasData =
    teamCount > 0;

  const [
    hoveredKey,
    setHoveredKey,
  ] = useState<HoveredBar>(null);

  /* ==========================================================
     TARGET
     ========================================================== */

  const targetPercentage =
    useMemo(() => {
      const target =
        Number(
          chartData[0]
            ?.targetPercentage,
        );

      if (
        Number.isFinite(target) &&
        target > 0
      ) {
        return target;
      }

      return 14;
    }, [chartData]);

  /* ==========================================================
     Y AXIS
     ========================================================== */

  const yAxisMax =
    useMemo(() => {
      if (!hasData) {
        return 30;
      }

      const values =
        chartData.flatMap(
          (item) => [
            Number(
              item.thisWeekIdle ?? 0,
            ),

            Number(
              item.lastWeekIdle ?? 0,
            ),

            Number(
              item.targetPercentage ??
                targetPercentage,
            ),
          ],
        );

      const maxValue =
        Math.max(
          ...values,
          0,
        );

      return Math.max(
        30,
        Math.ceil(
          (maxValue + 5) / 5,
        ) * 5,
      );
    }, [
      chartData,
      hasData,
      targetPercentage,
    ]);

  /* ==========================================================
     SCROLL RULE
     ========================================================== */

  /*
   * 1 - 5:
   *     responsive full row
   *     NO horizontal scrollbar
   *
   * 6+:
   *     wider chart
   *     horizontal scrollbar
   */
  const shouldScroll =
    teamCount > 5;

  /*
   * Minimum chart width only for 6+ teams.
   *
   * IMPORTANT:
   *
   * Do NOT apply this to <= 5 teams.
   * That is what keeps 1-5 teams fully responsive
   * when browser zoom changes.
   */
  const chartMinWidth =
    shouldScroll
      ? Math.max(
          720,
          teamCount * 105,
        )
      : 0;

  /* ==========================================================
     EMPTY STATE
     ========================================================== */

  if (!hasData) {
    return (
      <div
        className="
          box-border
          w-full
          min-w-0
          max-w-full
          overflow-hidden
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-5
        "
      >
        <div className="mb-5">
          <h3
            className="
              text-[15px]
              font-semibold
              text-slate-800
            "
          >
            Idle % by Team
          </h3>

          <p
            className="
              mt-1
              text-[11px]
              text-slate-500
            "
          >
            Team idle percentage compared
            with target.
          </p>
        </div>

        <div
          className="
            flex
            h-[300px]
            w-full
            items-center
            justify-center
            rounded-xl
            border
            border-dashed
            border-slate-300
          "
        >
          <span
            className="
              text-sm
              text-slate-500
            "
          >
            No Team Chart Data Available
          </span>
        </div>
      </div>
    );
  }

  /* ==========================================================
     MAIN CARD
     ========================================================== */

  return (
    <div
      className="
        box-border
        w-full
        min-w-0
        max-w-full
        overflow-hidden
        rounded-2xl
        border
        border-slate-200
        bg-white
        p-5
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          mb-4
          flex
          w-full
          min-w-0
          flex-wrap
          items-start
          justify-between
          gap-3
        "
      >
        <div
          className="
            min-w-0
            flex-1
          "
        >
          <h3
            className="
              truncate
              text-[15px]
              font-semibold
              text-slate-800
            "
          >
            Idle % by Team
          </h3>

          <p
            className="
              mt-1
              text-[11px]
              text-slate-500
            "
          >
            Team idle percentage compared
            with target.
          </p>
        </div>

        {/* ====================================================
            LEGEND
        ==================================================== */}

        <div
          className="
            flex
            max-w-full
            shrink-0
            flex-wrap
            items-center
            justify-end
            gap-x-5
            gap-y-2
          "
        >
          {/* Idle */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-2
            "
          >
            <div
              className="
                h-3
                w-3
                shrink-0
                rounded
                bg-orange-400
              "
            />

            <span
              className="
                whitespace-nowrap
                text-[11px]
                text-slate-600
              "
            >
              Idle %
            </span>
          </div>

          {/* Target */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-2
            "
          >
            <div
              className="
                h-[2px]
                w-6
                shrink-0
                border-t-2
                border-dashed
                border-red-500
              "
            />

            <span
              className="
                whitespace-nowrap
                text-[11px]
                text-slate-600
              "
            >
              Target ({targetPercentage}%)
            </span>
          </div>
        </div>
      </div>

      {/* ======================================================
          CHART OUTER WRAPPER
      ====================================================== */}

      <div
        className={
          shouldScroll
            ? `
              block
              w-full
              min-w-0
              max-w-full
              overflow-x-auto
              overflow-y-hidden
            `
            : `
              block
              w-full
              min-w-0
              max-w-full
              overflow-hidden
            `
        }
      >
        {/* ====================================================
            CHART INNER WRAPPER

            <= 5:
            100% responsive width.

            6+:
            minimum width creates scroll area.
        ==================================================== */}

        <div
          className="
            block
            w-full
            min-w-0
          "
          style={
            shouldScroll
              ? {
                  minWidth:
                    `${chartMinWidth}px`,
                }
              : undefined
          }
        >
          <ResponsiveContainer
            width="100%"
            height={320}
          >
            <BarChart
              data={chartData}
              barGap={4}
              barCategoryGap={
                teamCount <= 4
                  ? '22%'
                  : teamCount === 5
                    ? '12%'
                    : '30%'
              }
              margin={{
                top: 10,
                right: 15,
                left: 5,
                bottom: 32,
              }}
              onMouseLeave={() => {
                setHoveredKey(null);
              }}
            >
              {/* ==================================================
                  GRID
              ================================================== */}

              <CartesianGrid
                stroke="#ECECEC"
                vertical={false}
                strokeDasharray="2 2"
              />

              {/* ==================================================
                  X AXIS
              ================================================== */}

              <XAxis
                dataKey="teamLeadName"
                interval={0}
                height={62}
                tickLine={false}
                axisLine={false}
                padding={{
                  left:
                    teamCount <= 5
                      ? 2
                      : 8,
                  right:
                    teamCount <= 5
                      ? 2
                      : 8,
                }}
                tick={
                  <CustomXAxisTick
                    teamCount={teamCount}
                  />
                }
              />

              {/* ==================================================
                  Y AXIS
              ================================================== */}

              <YAxis
                domain={[
                  0,
                  yAxisMax,
                ]}
                ticks={Array.from(
                  {
                    length:
                      yAxisMax / 5 + 1,
                  },
                  (_, index) =>
                    index * 5,
                )}
                tickFormatter={(value) =>
                  `${value}%`
                }
                width={40}
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 11,
                  fill: '#64748B',
                }}
              />

              {/* ==================================================
                  LEGEND
              ================================================== */}

              <Legend
                verticalAlign="top"
                align="center"
                iconType="square"
                wrapperStyle={{
                  paddingBottom: 10,
                  fontSize: 11,
                }}
                formatter={(value) => {
                  const active =
                    hoveredKey === null ||
                    hoveredKey === value;

                  return (
                    <span
                      style={{
                        color: active
                          ? '#475569'
                          : '#CBD5E1',
                        cursor: 'pointer',
                        fontWeight:
                          hoveredKey === value
                            ? 600
                            : 400,
                        transition:
                          'color 150ms ease',
                      }}
                      onMouseEnter={() => {
                        setHoveredKey(
                          value as HoveredBar,
                        );
                      }}
                      onMouseLeave={() => {
                        setHoveredKey(null);
                      }}
                    >
                      {value ===
                      'lastWeekIdle'
                        ? 'Last Week Idle'
                        : 'This Week Idle'}
                    </span>
                  );
                }}
              />

              {/* ==================================================
                  TOOLTIP
              ================================================== */}

              <Tooltip
                shared={false}
                cursor={false}
                content={
                  <CustomTooltip />
                }
              />

              {/* ==================================================
                  TARGET
              ================================================== */}

              <ReferenceLine
                y={targetPercentage}
                stroke="#EF4444"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                ifOverflow="extendDomain"
                label={
                  <TargetLabel
                    value={
                      targetPercentage
                    }
                  />
                }
              />

              {/* ==================================================
                  THIS WEEK
              ================================================== */}

              <Bar
                dataKey="thisWeekIdle"
                name="thisWeekIdle"
                fill="#FF8C32"
                barSize={30}
                radius={[
                  0,
                  0,
                  0,
                  0,
                ]}
                fillOpacity={
                  hoveredKey === null ||
                  hoveredKey ===
                    'thisWeekIdle'
                    ? 1
                    : 0.3
                }
                isAnimationActive={false}
                onMouseEnter={() => {
                  setHoveredKey(
                    'thisWeekIdle',
                  );
                }}
                onMouseLeave={() => {
                  setHoveredKey(null);
                }}
              >
                <LabelList
                  dataKey="thisWeekIdle"
                  position="insideBottom"
                  formatter={(value) =>
                    Number(
                      value,
                    ).toFixed(2)
                  }
                  fill="#FFFFFF"
                  fontSize={9}
                  fontWeight={700}
                />
              </Bar>

              {/* ==================================================
                  LAST WEEK
              ================================================== */}

              <Bar
                dataKey="lastWeekIdle"
                name="lastWeekIdle"
                fill="#FFD54F"
                barSize={30}
                radius={[
                  0,
                  0,
                  0,
                  0,
                ]}
                fillOpacity={
                  hoveredKey === null ||
                  hoveredKey ===
                    'lastWeekIdle'
                    ? 1
                    : 0.3
                }
                isAnimationActive={false}
                onMouseEnter={() => {
                  setHoveredKey(
                    'lastWeekIdle',
                  );
                }}
                onMouseLeave={() => {
                  setHoveredKey(null);
                }}
              >
                <LabelList
                  dataKey="lastWeekIdle"
                  position="insideBottom"
                  formatter={(value) =>
                    Number(
                      value,
                    ).toFixed(2)
                  }
                  fill="#FFFFFF"
                  fontSize={9}
                  fontWeight={700}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default IdleTeamChart;